import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { ArrowLeft, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { getInstamojoSession, clearInstamojoSession } from './InstamojoPasswordGate';

const fmtINR = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtCount = (n) => Number(n || 0).toLocaleString('en-IN');

export default function InstamojoAddPayment() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;

  // Form state
  const [titleId, setTitleId]     = useState(0);
  const [titleText, setTitleText] = useState('');
  const [amount, setAmount]       = useState('');
  const [remark, setRemark]       = useState('');
  const [bankId, setBankId]       = useState(0);

  // Lookups
  const [banks, setBanks]   = useState([]);
  const [summary, setSummary] = useState({
    total_transaction: 0, amount_collected: 0, paid: 0, balance: 0,
  });

  // Purpose autocomplete
  const [acOpen, setAcOpen]   = useState(false);
  const [acItems, setAcItems] = useState([]);
  const acTimer = useRef(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    if (!getInstamojoSession()) navigate('/instamojo', { replace: true });
  }, [navigate]);

  // React Router doesn't reset scroll on route change — land at the top of the form.
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }, []);

  // Load existing record (edit mode)
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/instamojo-archive/payments/${editId}`);
        if (cancelled) return;
        const d = res.data;
        setTitleId(Number(d.fk_title_id) || 0);
        setTitleText(d.title || '');
        setAmount(d.amount != null ? String(d.amount) : '');
        setRemark(d.remark || '');
        setBankId(d.fk_bank_details_id || 0);
      } catch (err) {
        if (err.response?.status === 403) {
          clearInstamojoSession();
          toast.error('Session expired.');
          navigate('/instamojo', { replace: true });
          return;
        }
        toast.error(err.response?.data?.detail || 'Failed to load payment.');
        navigate('/instamojo/payments', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, editId, navigate]);

  // Whenever titleId changes, refresh summary cards + bank list.
  // Rotary's Add Payment page calls GetIntamojoPaymentDetails_Page even when
  // fk_title_id="0" — the SP returns global aggregates across all purposes.
  // We mirror that: always hit /payments/summary (omit title_id when 0/falsy
  // → backend treats absent param as "all purposes"). Banks dropdown is
  // per-purpose only, so it stays empty until a Purpose is picked.
  const refreshContext = useCallback(async (tid) => {
    try {
      const sumPromise = api.get('/instamojo-archive/payments/summary', {
        params: tid ? { title_id: tid } : {},
      });
      const banksPromise = tid
        ? api.get(`/instamojo-archive/purposes/${tid}/banks`)
        : Promise.resolve({ data: { banks: [] } });

      const [sumRes, banksRes] = await Promise.all([sumPromise, banksPromise]);
      setSummary(sumRes.data);
      setBanks(banksRes.data?.banks || []);
    } catch (err) {
      if (err.response?.status === 403) {
        clearInstamojoSession();
        toast.error('Session expired.');
        navigate('/instamojo', { replace: true });
      }
    }
  }, [navigate]);

  useEffect(() => { refreshContext(titleId); }, [titleId, refreshContext]);

  // ── Purpose autocomplete ─────────────────────────────────────────────────
  const onTitleChange = (e) => {
    const v = e.target.value;
    setTitleText(v);
    setTitleId(0);
    setBankId(0);
    if (acTimer.current) clearTimeout(acTimer.current);
    if (v.trim().length < 3) { setAcOpen(false); setAcItems([]); return; }
    acTimer.current = setTimeout(async () => {
      try {
        const res = await api.get('/instamojo-archive/purposes/autocomplete', {
          params: { q: v.trim(), limit: 10 },
        });
        setAcItems(res.data || []);
        setAcOpen(true);
      } catch {/* ignore */}
    }, 250);
  };
  const pickTitle = (item) => {
    setTitleText(item.title || '');
    setTitleId(item.id);
    setAcOpen(false);
  };
  const clearTitle = () => {
    setTitleText('');
    setTitleId(0);
    setBankId(0);
    setAcOpen(false);
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!titleId)              { toast.error('Purpose is mandatory.'); return; }
    if (!amount || !amount.toString().trim())
                                { toast.error('Amount is mandatory.'); return; }
    const num = Number(amount);
    if (!Number.isFinite(num) || num <= 0) {
      toast.error('Enter only numbers');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        titleId,
        amount: num,
        remark: remark.trim() || null,
        bankDetailsId: bankId > 0 ? bankId : null,
      };
      if (isEdit) {
        const res = await api.patch(`/instamojo-archive/payments/${editId}`, payload);
        toast.success(res.data?.message || 'Payment Updated Successfully');
      } else {
        const res = await api.post('/instamojo-archive/payments', payload);
        toast.success(res.data?.message || 'Payment added Successfully');
      }
      navigate('/instamojo/payments', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }

  const Stat = ({ label, value, isCount = false }) => (
    <Card className="p-4 text-center bg-white">
      <div className="text-2xl font-bold text-sky-700">{isCount ? fmtCount(value) : fmtINR(value)}</div>
      <div className="mt-1 text-sm font-medium text-slate-600">{label}</div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-3 md:p-4">
      <div className="w-full max-w-6xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => navigate('/instamojo/payments')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-bold text-slate-900">Add Payment Details</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Form */}
          <Card className="p-5">
            <div className="space-y-3">
              {/* Purpose autocomplete */}
              <div className="relative">
                <Label>Purpose <span className="text-red-600">*</span></Label>
                <div className="flex gap-2">
                  <Input value={titleText}
                    onChange={onTitleChange}
                    onFocus={() => acItems.length && setAcOpen(true)}
                    onBlur={() => setTimeout(() => setAcOpen(false), 150)}
                    placeholder="Enter Purpose (min 3 chars)"
                    disabled={isEdit /* match Rotary: title locked when editing */} />
                  {!isEdit && titleText && (
                    <Button variant="outline" size="icon" onClick={clearTitle} title="Clear">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {acOpen && acItems.length > 0 && (
                  <div className="absolute z-20 mt-1 w-[calc(100%-3rem)] bg-white border border-slate-200 rounded-md shadow max-h-64 overflow-y-auto">
                    {acItems.map((it) => (
                      <button key={it.id}
                        type="button"
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-violet-50"
                        onMouseDown={(e) => { e.preventDefault(); pickTitle(it); }}>
                        {it.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Bank list */}
              <div>
                <Label>Bank list</Label>
                <select value={bankId}
                  onChange={(e) => setBankId(Number(e.target.value))}
                  className="w-full h-10 rounded-md border border-slate-200 px-2 bg-white text-sm">
                  <option value={0}>Select</option>
                  {banks.map((b) => (
                    <option key={b.pk_purposetitle_bank_details_id} value={b.pk_purposetitle_bank_details_id}>
                      {[b.account_name, b.bank_name, b.account_number].filter(Boolean).join(' | ')}
                    </option>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div>
                <Label>Amount <span className="text-red-600">*</span></Label>
                <Input value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                  placeholder="0.00"
                  inputMode="decimal" />
              </div>

              {/* Remark */}
              <div>
                <Label>Remark</Label>
                <Input value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Remark" />
              </div>

              <div className="text-center pt-3">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : (isEdit ? 'Update' : 'Save')}
                </Button>
              </div>
            </div>
          </Card>

          {/* Summary cards (right column on desktop) */}
          <div className="grid grid-cols-2 gap-3 content-start">
            <Stat label="Total Transaction" value={summary.total_transaction} isCount />
            <Stat label="Amount Collected"  value={summary.amount_collected} />
            <Stat label="Paid"               value={summary.paid} />
            <Stat label="Balance"            value={summary.balance} />
          </div>
        </div>
      </div>
    </div>
  );
}
